import { KeycloakService } from 'keycloak-angular';
import {Login} from "../models/auth.model";
import {Store} from "@ngxs/store";
import {environment} from "../../environments/environment";
import { isReviewEnvironment } from '../../environments/review-utils';
import { clearKeycloakLoginRedirect, keycloakLogin } from '../services/common/keycloak-login-redirect';

const KEYCLOAK_INIT_RECOVERY_KEY = 'keycloak-init-recovery-at';
const KEYCLOAK_INIT_RECOVERY_TTL_MS = 30000;

const getRealmFromDomain = () => {
  const {hostname} = globalThis.location;
  const DEFAULT_REALM = environment.keycloak.realm;

  // handle admin exception - we have to use "gapmaps" realm
  if (hostname === 'admin.gapmaps.com' || hostname === 'staging-admin.gapmaps.com') {
    return DEFAULT_REALM;
  }

  // handle review instance override
  if(isReviewEnvironment(globalThis.location)) {
    return DEFAULT_REALM;
  }

  const subdomainMatchStaging = hostname.match(/staging-admin-(.+)\.gapmaps\.com$/);
  if (subdomainMatchStaging) return subdomainMatchStaging[1];

  const subdomainMatchProd = hostname.match(/admin-(.+)\.gapmaps\.com$/);
  if (subdomainMatchProd) return subdomainMatchProd[1];

  const directSubdomainMatch = hostname.match(/(.+)\.gapmaps\.com$/);
  if (directSubdomainMatch) return directSubdomainMatch[1];

  return DEFAULT_REALM;
};

const isInitRecoveryActive = () => {
  const startedAt = Number(sessionStorage.getItem(KEYCLOAK_INIT_RECOVERY_KEY));
  if (!startedAt) {
    return false;
  }

  if (Date.now() - startedAt > KEYCLOAK_INIT_RECOVERY_TTL_MS) {
    sessionStorage.removeItem(KEYCLOAK_INIT_RECOVERY_KEY);
    return false;
  }

  return true;
};

const clearInitRecovery = () => {
  sessionStorage.removeItem(KEYCLOAK_INIT_RECOVERY_KEY);
};

const cleanupRecoveryUrl = () => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('ngsw-bypass') && !url.searchParams.has('keycloak-init-recovery')) {
    return;
  }

  url.searchParams.delete('ngsw-bypass');
  url.searchParams.delete('keycloak-init-recovery');
  window.history.replaceState(window.history.state, document.title, url.toString());
};

const attemptServiceWorkerRecovery = () => {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller || isInitRecoveryActive()) {
    return false;
  }

  sessionStorage.setItem(KEYCLOAK_INIT_RECOVERY_KEY, String(Date.now()));

  const url = new URL(window.location.href);
  url.searchParams.set('ngsw-bypass', 'true');
  url.searchParams.set('keycloak-init-recovery', '1');
  window.location.replace(url.toString());
  return true;
};

export function initializeKeycloak(keycloak: KeycloakService, store: Store) {
  const realmName = getRealmFromDomain();
  const initOptions = {
    config: {
      url: `${environment.keycloak.url}`,
      realm: realmName,
      clientId: `${environment.keycloak.clientId}`,
    },
    initOptions: {
      onLoad: 'check-sso' as const,
      checkLoginIframe: false,
      silentCheckSsoRedirectUri:
        window.location.origin + '/assets/silent-check-sso.html',
    },
    bearerExcludedUrls: ['/assets'],
  };

  const runInit = async (): Promise<boolean | void> => {
    try {
      const authenticated = await keycloak.init(initOptions);
      clearInitRecovery();
      cleanupRecoveryUrl();

      if (authenticated) {
        const keycloakInstance = keycloak.getKeycloakInstance();
        clearKeycloakLoginRedirect();
        const authData = {
          username: keycloakInstance.tokenParsed.email,
          token: keycloakInstance.token,
          refresh_token: keycloakInstance.refreshToken,
          expires_at: keycloakInstance.tokenParsed.exp * 1000,
          authenticated: true,
        }
        store.dispatch(new Login(authData))
      } else {
        return keycloakLogin(keycloak);
      }

      return authenticated;
    } catch (err) {
      console.log('Error initializing Keycloak: ', err);

      if (attemptServiceWorkerRecovery()) {
        return false;
      }

      clearInitRecovery();
      return keycloakLogin(keycloak);
    }
  };

  return () => runInit();
}
